// ─────────────────────────────────────────────
// KLOTHIQ — Payment Form Page (standalone)
// For quick cash-in / cash-out recording
// ─────────────────────────────────────────────

import { Input, Select } from '@/components/ui/FormControls';
import DatePicker from '@/components/common/DatePicker';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecordPayment } from '@/hooks/useSales';
import { useRecordExpense } from '@/hooks/useCash';
import { useCashAccounts } from '@/hooks/useCash';
import { useExpenseCategories } from '@/hooks/useAccounts';
import PageHeader from '@/components/common/PageHeader';
import { Loader2, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';

const inputCls = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground';

type Mode = 'expense' | 'income';

export default function PaymentFormPage() {
  const navigate = useNavigate();
  const { data: accounts } = useCashAccounts();
  const { data: categories } = useExpenseCategories();
  const recordPayment = useRecordPayment();
  const recordExpense = useRecordExpense();

  const today = new Date().toISOString().split('T')[0]!;
  const [mode, setMode] = useState<Mode>('expense');
  const [amount, setAmount] = useState('');
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [particular, setParticular] = useState('');
  const [method, setMethod] = useState('cash');
  const [date, setDate] = useState(today);
  const [ref, setRef] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountId) { toast.error('Please select a cash account'); return; }
    if (!parseFloat(amount) || parseFloat(amount) <= 0) { toast.error('Please enter a valid amount'); return; }
    if (!particular.trim()) { toast.error('Please enter a description'); return; }
    if (!date) { toast.error('Date is required'); return; }

    setIsLoading(true);
    try {
      if (mode === 'expense') {
        await recordExpense.mutateAsync({
          account_id: accountId,
          amount: parseFloat(amount),
          particular,
          expense_date: date,
          expense_category_id: categoryId || null,
          payment_method: method,
        });
        toast.success('Expense recorded successfully');
        navigate('/accounts/expenses');
      } else {
        await recordPayment.mutateAsync({
          payment_type: 'sale_payment',
          account_id: accountId,
          amount: parseFloat(amount),
          payment_method: method,
          payment_date: date,
          reference_number: ref || undefined,
          notes: particular,
        });
        toast.success('Payment recorded successfully');
        navigate('/accounts/balances');
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Transaction failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Record Transaction"
        description="Quick cash in or expense entry"
        backTo="/accounts/balances"
      />

      <div className="mx-auto max-w-md">
        {/* Mode toggle */}
        <div className="mb-4 flex rounded-md border border-border bg-muted/30 p-0.5">
          {(['expense', 'income'] as Mode[]).map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)}
              className={`flex-1 rounded py-2 text-sm font-medium transition-colors capitalize ${
                mode === m ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {m === 'expense' ? '↑ Expense' : '↓ Income'}
            </button>
          ))}
        </div>

        <div className="obsidian-surface rounded-lg border border-border bg-card p-5 shadow-sm">
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="tx-amount" className="text-sm font-medium text-foreground">
                Amount (₹) <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="tx-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className={`${inputCls} pl-8`}
                  helpLabel="Amount (₹) *"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="tx-account" className="text-sm font-medium text-foreground">
                Account <span className="text-destructive">*</span>
              </label>
              <Select
                id="tx-account"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className={inputCls}
                helpLabel="Account *"
              >
                <option value="">Select account</option>
                {accounts?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} — ₹{Number(a.current_balance).toLocaleString('en-IN')}
                  </option>
                ))}
              </Select>
            </div>

            {mode === 'expense' && (
              <div className="space-y-1.5">
                <label htmlFor="tx-category" className="text-sm font-medium text-foreground">
                  Expense Category
                </label>
                <Select
                  id="tx-category"
                  value={categoryId}
                  onChange={(e) => {
                    const newCat = e.target.value;
                    setCategoryId(newCat);
                    const catObj = categories?.find((c) => c.id === newCat);
                    if (catObj && (!particular || categories?.some((c) => c.name === particular))) {
                      setParticular(catObj.name);
                    }
                  }}
                  className={inputCls}
                  helpLabel="Expense Category"
                >
                  <option value="">Select category (optional)</option>
                  {categories?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="tx-particular" className="text-sm font-medium text-foreground">
                Description <span className="text-destructive">*</span>
              </label>
              <Input
                id="tx-particular"
                value={particular}
                onChange={(e) => setParticular(e.target.value)}
                placeholder={mode === 'expense' ? 'e.g. Office supplies' : 'e.g. Cash sale'}
                className={inputCls}
                helpLabel="Description *"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="tx-date" className="text-sm font-medium text-foreground">Date *</label>
                <DatePicker
                  disabled={isLoading}
                  today={today}
                  clearable={false}
                  aria-label="Date"
                  id="tx-date"
                  value={date}
                  onChange={(nextDate) => setDate(nextDate ?? '')}
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="tx-method" className="text-sm font-medium text-foreground">Method</label>
                <Select
                  id="tx-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className={inputCls}
                  helpLabel="Method"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </Select>
              </div>
            </div>

            {mode === 'income' && (
              <div className="space-y-1.5">
                <label htmlFor="tx-ref" className="text-sm font-medium text-foreground">Reference #</label>
                <Input
                  id="tx-ref"
                  value={ref}
                  onChange={(e) => setRef(e.target.value)}
                  placeholder="UTR / UPI ID / Cheque #"
                  className={inputCls}
                  helpLabel="Reference #"
                />
              </div>
            )}

            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <button type="button" onClick={() => navigate('/accounts/balances')}
                className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent">Cancel</button>
              <button type="submit" disabled={isLoading}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Record {mode === 'expense' ? 'Expense' : 'Income'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
